/**
 * R-71 / R-72 — detect classifier outputs that collapsed to baseline defaults.
 *
 * Pure helper: no I/O. Used by the provider coverage gate when evaluating
 * the persisted `publicSentiment` SED section.
 */
import type { PerTechSentiment, PublicSentimentSection } from '../types/siteEvidence';
import { PUBLIC_SENTIMENT_BTM_TECHS } from '../services/publicSentimentOrchestrator';

const MIN_SAMPLES_FOR_VARIANCE = 5;
const MAX_STD_DEV_FOR_BASELINE = 0.001;

function stdDev(values: readonly number[]): number {
  if (values.length < 2) return 0;
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const variance = values.reduce((acc, v) => acc + (v - mean) ** 2, 0) / values.length;
  return Math.sqrt(variance);
}

function perTechTotalCount(perTech: readonly PerTechSentiment[]): number {
  return perTech.reduce((sum, b) => sum + b.count, 0);
}

function perTechUniformBaseline(perTech: readonly PerTechSentiment[]): boolean {
  const buckets = PUBLIC_SENTIMENT_BTM_TECHS.map(tech =>
    perTech.find(p => p.btmTech === tech),
  ).filter((b): b is PerTechSentiment => b != null);

  if (buckets.length !== PUBLIC_SENTIMENT_BTM_TECHS.length) return false;
  const totalCount = perTechTotalCount(buckets);
  if (totalCount < MIN_SAMPLES_FOR_VARIANCE) return false;

  const sentiments = buckets.map(b => b.meanSentiment);
  return stdDev(sentiments) < MAX_STD_DEV_FOR_BASELINE;
}

/**
 * Returns true when sentiment observations look like classifier defaults
 * rather than real variance (SWC stub-report failure mode).
 */
export function detectBaselineSentiment(section: PublicSentimentSection | null | undefined): boolean {
  if (!section) return false;

  const { entries, perTech } = section;

  if (entries.length >= MIN_SAMPLES_FOR_VARIANCE) {
    const scores = entries.map(e => e.sentimentScore).filter(Number.isFinite);
    if (scores.length >= MIN_SAMPLES_FOR_VARIANCE && stdDev(scores) < MAX_STD_DEV_FOR_BASELINE) {
      return true;
    }
  }

  if (perTechUniformBaseline(perTech)) {
    return true;
  }

  // Zero entries with partial/ok status — orchestrator shipped empty aggregates
  if (entries.length === 0 && section.status !== 'unavailable') {
    const totalCount = perTechTotalCount(perTech);
    if (totalCount === 0) return true;
  }

  return false;
}
